import { ForwardedRef, forwardRef, useCallback, useState } from 'react';
import {
  Dimensions,
  FlatList,
  LayoutChangeEvent,
  ListRenderItem,
} from 'react-native';
import { Box, List, useBreakpointProp } from '@bonanza-social/moonlight';
import { GamesGridProps } from './GamesGrid.types';
import GamesGridSkeleton from './Skeleton/GamesGridSkeleton';
import Game from '../Games/Game/Game';
import { useResponsiveGridItemWidth } from '@bonanza-social/toolkit/hooks';
import { GRID_GAP, DEFAULT_GRID_OFFSET_X } from './GamesGrid.constants';

function GamesGrid(
  {
    gameIds,
    loading,
    imageType,
    gridOffsetX = DEFAULT_GRID_OFFSET_X,
    contentContainerStyle,
    currencyCode,
    ListHeaderComponent,
    onGamePress,
    gameHighlightedId,
    numOfColumns,
    ...rest
  }: GamesGridProps,
  ref: ForwardedRef<FlatList>,
) {
  const numberOfColumns = useBreakpointProp({
    mobile: 3,
    tablet: 4,
    desktop: 8,
  });

  const [listWidth, setListWidth] = useState(Dimensions.get('window').width);

  const onLayout = useCallback((event: LayoutChangeEvent) => {
    setListWidth(event.nativeEvent.layout.width);
  }, []);

  const itemWidth = useResponsiveGridItemWidth({
    listWidth,
    numberOfColumns: numOfColumns ?? numberOfColumns,
    gridGap: GRID_GAP,
    gridOffsetX,
  });

  const renderSeparator = useCallback(
    () => <Box width={GRID_GAP} height={GRID_GAP} />,
    [],
  );

  const renderItem = useCallback<ListRenderItem<(typeof gameIds)[number]>>(
    ({ item }) => (
      <Game
        key={item}
        id={item}
        flex={1}
        maxWidth={itemWidth}
        dynamicSize
        imageType={imageType}
        currencyCode={currencyCode}
        isCoinsGameplay={currencyCode === 'BTY'}
        onPress={onGamePress}
        highlighted={item === gameHighlightedId}
      />
    ),
    [itemWidth, imageType, currencyCode, onGamePress, gameHighlightedId],
  );

  if (loading) {
    return (
      <Box flex={1}>
        <Box maxWidth="max-page-width" marginHorizontal="auto">
          <GamesGridSkeleton
            itemsPerRow={numberOfColumns}
            imageType={imageType}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box flex={1}>
      <List
        ref={ref}
        onLayout={onLayout}
        data={gameIds}
        windowSize={4}
        key={numOfColumns ?? numberOfColumns}
        numColumns={numOfColumns ?? numberOfColumns}
        ListHeaderComponent={ListHeaderComponent}
        initialNumToRender={10}
        renderItem={renderItem}
        ItemSeparatorComponent={renderSeparator}
        keyboardDismissMode="interactive"
        keyboardShouldPersistTaps="handled"
        columnWrapperStyle={{
          gap: GRID_GAP,
          flexDirection: 'row',
          flexWrap: 'wrap',
          width: '100%',
          maxWidth: 'max-page-width',
          marginHorizontal: 'auto',
        }}
        contentContainerStyle={{
          ...contentContainerStyle,
          paddingHorizontal: gridOffsetX,
          flexGrow: 1,
        }}
        {...rest}
      />
    </Box>
  );
}

export default forwardRef(GamesGrid);
